/**
 * COMMERCE-CORE-R1 — Customer Cabinet order-centric document home.
 * Groups by STG; opens order document bundle (order detail documents tab).
 */
import { useEffect, useState } from "react";
import type { StoreV2Lang } from "./store-v2-locale.js";
import {
  fetchMyOrdersBounded,
  type CustomerOrderSummary,
} from "./order-history-v2.js";

export function CustomerOrderDocumentsHomeV2({
  lang,
  sessionKey,
  onOpenOrderDocuments,
  onOpenLegalHub,
  onBack,
}: {
  lang: StoreV2Lang;
  sessionKey: string;
  onOpenOrderDocuments: (orderId: string) => void;
  onOpenLegalHub?: () => void;
  onBack: () => void;
}) {
  const en = lang === "en";
  const [orders, setOrders] = useState<CustomerOrderSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchMyOrdersBounded(sessionKey).then((outcome) => {
      if (cancelled) return;
      if (!outcome.ok) {
        setError(en ? "Could not load orders." : "Не вдалося завантажити замовлення.");
        setOrders([]);
        return;
      }
      setOrders(outcome.data);
      setError(null);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionKey, en]);

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
    return d.toLocaleDateString(en ? "en-GB" : "uk-UA", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const docCount = (o: CustomerOrderSummary) =>
    typeof o.documents_count === "number" ? o.documents_count : null;

  return (
    <div className="sv2-body" data-testid="sv2-order-documents-home">
      <div className="sv2-section-title">{en ? "Documents" : "Документи"}</div>
      <p className="sv2-muted" style={{ marginBottom: 12, fontSize: 13 }}>
        {en
          ? "Order documents are grouped by order number (STG)."
          : "Документи згруповано за номером замовлення (STG)."}
      </p>
      {error ? <p className="sv2-alert sv2-alert-err">{error}</p> : null}
      {orders == null ? (
        <p className="sv2-muted" data-testid="sv2-order-documents-loading">
          {en ? "Loading…" : "Завантаження…"}
        </p>
      ) : orders.length === 0 ? (
        <div className="sv2-info-card sv2-cabinet-card" data-testid="sv2-order-documents-empty">
          <p className="sv2-muted" style={{ margin: 0 }}>
            {en
              ? "No orders yet. Documents will appear here after an order is created."
              : "Поки немає замовлень. Документи з’являться тут після створення замовлення."}
          </p>
        </div>
      ) : (
        <ul
          className="sv2-about-bento"
          style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 10 }}
          data-testid="sv2-order-documents-list"
        >
          {orders.map((o) => {
            const stg = String(o.display_order_number || "").trim() || o.order_id;
            const count = docCount(o);
            const statusUk = o.documents_status_uk ?? null;
            return (
              <li key={o.order_id}>
                <button
                  type="button"
                  className="sv2-about-bento-tile"
                  style={{ width: "100%", textAlign: "left", minHeight: 72 }}
                  data-testid="sv2-order-documents-card"
                  data-stg={stg.startsWith("STG-") ? stg : undefined}
                  data-public-order-number={stg.startsWith("STG-") ? stg : undefined}
                  data-canonical-order-ref={o.order_id}
                  onClick={() => onOpenOrderDocuments(o.order_id)}
                >
                  <span className="sv2-cabinet-tile-label" style={{ display: "block", fontWeight: 700 }}>
                    {en ? "Order" : "Замовлення"} {stg.startsWith("STG-") ? stg : stg}
                  </span>
                  <span className="sv2-muted" style={{ display: "block", fontSize: 13, marginTop: 4 }}>
                    {count == null
                      ? en
                        ? "Open documents"
                        : "Відкрити документи"
                      : en
                        ? `${count} document${count === 1 ? "" : "s"}`
                        : `${count} ${count === 1 ? "документ" : count < 5 ? "документи" : "документів"}`}
                    {" · "}
                    {formatDate(o.created_at)}
                  </span>
                  {statusUk ? (
                    <span className="sv2-muted" style={{ display: "block", fontSize: 12, marginTop: 4 }}>
                      {statusUk}
                    </span>
                  ) : null}
                  <span className="sv2-link" style={{ display: "inline-block", marginTop: 8, fontSize: 13 }}>
                    {en ? "Open" : "Відкрити"}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {onOpenLegalHub ? (
        <button
          type="button"
          className="sv2-cta sv2-secondary"
          style={{ marginTop: 16, width: "100%" }}
          data-testid="sv2-order-documents-legal-hub"
          onClick={onOpenLegalHub}
        >
          {en ? "Store legal documents" : "Юридичні документи магазину"}
        </button>
      ) : null}
      <button
        type="button"
        className="sv2-cta sv2-secondary"
        style={{ marginTop: 8, width: "100%" }}
        data-testid="sv2-order-documents-back"
        onClick={onBack}
      >
        {en ? "Back" : "Назад"}
      </button>
    </div>
  );
}
